import * as React from 'react';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';

export type StatusDotVariant = 'successful' | 'neutral' | 'warning' | 'destructive';

export interface StatusDotProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Semantic status that picks the dot colour
   */
  variant?: StatusDotVariant;

  /**
   * Tailwind bg colour class, for colours no variant covers. Overrides `variant`.
   * @example "bg-informational"
   */
  color?: string;

  /**
   * Size of the dot
   * @default "sm"
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Optional tooltip text to show on hover
   */
  tooltip?: string;

  /**
   * Delay before showing tooltip (in milliseconds)
   * @default 0
   */
  tooltipDelayDuration?: number;

  /**
   * Side where the tooltip should appear
   */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Max-width of the tooltip content. Forwarded to InfoTooltip.
   */
  tooltipSize?: 'default' | 'large' | 'auto';
}

const sizeClasses = { sm: 'h-2 w-2', md: 'h-3 w-3', lg: 'h-4 w-4' };

const variantClasses: Record<StatusDotVariant, string> = {
  successful: 'bg-successful',
  neutral: 'bg-foreground-muted',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
};

/**
 * Generic status dot component with shadow
 * Displays a colored dot with optional tooltip
 */
export const StatusDot = ({
  variant = 'neutral',
  color,
  size = 'sm',
  tooltip,
  tooltipDelayDuration = 0,
  tooltipSide,
  tooltipSize,
  className,
  ref,
  ...props
}: StatusDotProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const dotElement = (
    <div ref={ref} className={cn('flex items-center justify-center', className)} {...props}>
      <div
        className={cn(
          'rounded-full border border-element-primary-foreground shadow-[0px_1px_2px_-1px_#0000001A,0px_1px_3px_0px_#0000001A]',
          sizeClasses[size],
          color || variantClasses[variant]
        )}
      />
    </div>
  );

  if (tooltip) {
    return (
      <InfoTooltip
        content={tooltip}
        delayDuration={tooltipDelayDuration}
        side={tooltipSide}
        size={tooltipSize}
      >
        {dotElement}
      </InfoTooltip>
    );
  }

  return dotElement;
};
